import _get from 'lodash/get'
import { delay } from 'bluebird'
import { getZuoraIframeSignatureToken } from '.'
import * as actions from './redux/actions'

function shouldLoadZuoraIframeScript ({ state }) {
  const { config } = state
  if (config.getIn(['data', 'features', 'zuoraIframe'])) {
    return true
  }

  return false
}

async function loadZuoraScript (onSuccess, onError, attempt = 0, maxAttempt = 3) {
  const scriptId = 'zuoraIframeScript'
  const existingScript = document.getElementById(scriptId)

  if (!existingScript) {
    const script = document.createElement('script')
    script.src = 'https://static.zuora.com/Resources/libs/hosted/1.3.1/zuora-min.js'
    script.id = scriptId
    document.body.appendChild(script)

    script.onload = () => {
      if (onSuccess) {
        onSuccess()
      }
    }

    script.onerror = async () => {
      if (onError) {
        onError()
      }

      // delete the script tag since there was an error
      document.getElementById(scriptId).remove()

      // try to fetch the script again until we reach the maxAttempt limit
      if (attempt < maxAttempt) {
        await delay(500)
        loadZuoraScript(onSuccess, onError, attempt + 1)
      }
    }
  }

  if (existingScript && onSuccess) {
    onSuccess()
  }
}

// -----------------------------------
// Watcher for GET_ZUORA_IFRAME_SIGNATURE_TOKEN
// -----------------------------------
export function watchGetZuoraIframeSignatureToken ({ after }) {
  return after(actions.GET_ZUORA_IFRAME_SIGNATURE_TOKEN, async ({ state, dispatch }) => {
    const { config } = state
    try {
      const pageId = config.getIn(['data', 'zuora', 'creditCardPageId'])
      const uri = config.getIn(['data', 'zuora', 'iframeUrl'])
      const server = config.getIn(['data', 'servers', 'brooklyn'])

      dispatch({
        type: actions.SET_ZUORA_IFRAME_SIGNATURE_TOKEN_PROKESSING,
        payload: true,
      })

      const data = await getZuoraIframeSignatureToken({ pageId, uri, server })

      dispatch({
        type: actions.SET_ZUORA_IFRAME_SIGNATURE_TOKEN_DATA,
        payload: { data, processing: false },
      })
    } catch (e) {
      dispatch({
        type: actions.SET_ZUORA_IFRAME_SIGNATURE_TOKEN_DATA,
        payload: { data: { success: false }, processing: false },
      })
    }
  })
}

// -----------------------------------
// Watcher for SET_ZUORA_IFRAME_SIGNATURE_TOKEN_DATA
// -----------------------------------
export function watchSetZuoraIframeSignatureTokenData ({ after }) {
  return after(actions.SET_ZUORA_IFRAME_SIGNATURE_TOKEN_DATA, async ({ state, dispatch }) => {
    const { zuora } = state
    const tokenSuccess = zuora.getIn(['data', 'iframe', 'rsa', 'success'])

    if (tokenSuccess) {
      const onSuccess = () => dispatch({
        type: actions.SET_ZUORA_IFRAME_SCRIPT_LOADED,
        payload: true,
      })

      const onError = () => {
        dispatch({
          type: actions.SET_ZUORA_IFRAME_SCRIPT_LOADED,
          payload: false,
        })
      }

      await loadZuoraScript(onSuccess, onError)
    }
  })
    .when(shouldLoadZuoraIframeScript)
}

// -----------------------------------
// Watcher for SET_ZUORA_IFRAME_CLIENT_READY
// -----------------------------------
export function watchSetZuoraIframeClientReady ({ before }) {
  return before(actions.SET_ZUORA_IFRAME_CLIENT_READY, async ({ state, action, dispatch }) => {
    const { zuora, config } = state
    const { payload } = action
    const pageId = config.getIn(['data', 'zuora', 'creditCardPageId'])
    const uri = config.getIn(['data', 'zuora', 'iframeUrl'])
    const previousClientReady = zuora.getIn(['data', 'iframe', 'clientReady'])
    const nextClientReady = payload

    const { Z } = global

    const iframeSubmitCallback = (response) => {
      if (!response.success || response.success !== 'true') {
        return dispatch({
          type: actions.SET_ZUORA_PAYMENT_TOKEN_ERROR,
          payload: 'paymentError',
        })
      }

      return dispatch({
        type: actions.SET_ZUORA_PAYMENT_TOKEN_DATA,
        payload: {
          refId: _get(response, ['refId']),
          token: _get(response, ['token']),
          country: _get(response, ['creditCardCountry']),
          postalCode: _get(response, ['creditCardPostalCode']),
        },
      })
    }

    const errorMessageCallback = (key, code, message) => {
      if (key === 'error') {
        dispatch({
          type: actions.SET_ZUORA_PAYMENT_TOKEN_ERROR,
          payload: 'paymentTokenError',
        })
      }

      // there was an error so halt processing
      dispatch({
        type: actions.SET_ZUORA_PAYMENT_TOKEN_PROCESSING,
        payload: false,
      })

      Z.sendErrorMessageToHpm(key, message)
    }

    const onRenderComplete = () => {
      // tell the client that render is complete, so display the iframe
      dispatch({
        type: actions.SET_ZUORA_IFRAME_RENDER_COMPLETE,
        payload: true,
      })
    }

    if (Z && nextClientReady && nextClientReady !== previousClientReady) {
      // pre-populated values
      const prepopulate = {
        creditCardCountry: 'USA',
      }

      // set up the params to render the iframe
      const iframeParams = {
        signature: zuora.getIn(['data', 'iframe', 'rsa', 'signature']),
        token: zuora.getIn(['data', 'iframe', 'rsa', 'token']),
        tenantId: zuora.getIn(['data', 'iframe', 'rsa', 'tenantId']),
        key: zuora.getIn(['data', 'iframe', 'rsa', 'key']),
        id: pageId,
        locale: 'en',
        url: uri,
        style: 'inline',
        retainValues: 'true',
        submitEnabled: 'true',
      }

      // render the iframe
      // documentation for renderWithErrorHandler can be found at:
      // https://knowledgecenter.zuora.com/Billing/Billing_and_Payments/LA_Hosted_Payment_Pages/B_Payment_Pages_2.0/N_Error_Handling_for_Payment_Pages_2.0/Customize_Error_Messages_for_Payment_Pages_2.0#Invoke_the_Alternate_Render_Function_for_Custom_Error_Handling
      Z.renderWithErrorHandler(
        iframeParams,
        prepopulate,
        iframeSubmitCallback,
        errorMessageCallback,
      )

      // runs when render is complete
      Z.runAfterRender(onRenderComplete)
    }
  })
    .when(shouldLoadZuoraIframeScript)
}
